<template>
    <v-menu v-model="menu" :close-on-content-click="false" offset-y location="bottom start">
        <template v-slot:activator="{ props }">
            <div v-bind="props" class="station-selector-trigger">
                <slot></slot>
            </div>
        </template>

        <v-card class="station-selector-card" elevation="3" min-width="340" max-width="340">
            <div class="d-flex align-center gap-2 pa-4 pb-4 selector-header" >
                <v-text-field v-model="searchName" placeholder="输入电站名称" rounded variant="outlined" density="compact"
                    hide-details class="search-input flex-grow-1" append-inner-icon="mdi-magnify" clearable
                    @click:clear="handleSearch" @click:append-inner="handleSearch" @keyup.enter="handleSearch"></v-text-field>

                <v-select v-model="selectedArea" class="region-select" placeholder="按区域" variant="outlined"
                    density="compact" rounded hide-details clearable :items="['东北', '华中', '华南', '西南', '西北']"
                    @update:modelValue="handleFilterChange"></v-select>
            </div>

            <v-infinite-scroll class="station-list custom-scrollbar" height="660px" :items="list" :onLoad="loadMore" side="end">
                <template v-if="list.length > 0">
                    <div v-for="item in list" :key="item.id" class="station-item"
                        :class="{ 'active': currentStationId === item.id }" @click="handleSelect(item)">
                        <div class="station-name text-truncate">{{ item.name }}</div>
                    </div>
                </template>

                <template v-else>
                    <div v-if="loading" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
                    </div>
                    <div v-else class="text-center py-4 text-grey text-caption">
                        暂无数据
                    </div>
                </template>

                <template #loading>
                    <!-- <div class="text-center py-2">
                        <v-progress-circular indeterminate color="primary" size="20"></v-progress-circular>
                    </div> -->
                </template>
                <template #empty>
                </template>
            </v-infinite-scroll>
        </v-card>
    </v-menu>
</template>

<script setup>
import { ref, watch } from 'vue'
import { getMyStationList } from '@/api/home'

const props = defineProps({
    currentStationId: {
        type: [String, Number],
        default: ''
    }
})

const emit = defineEmits(['change'])

const menu = ref(false)
const searchName = ref('')
const selectedArea = ref(null)
const list = ref([])
const loading = ref(false)

// 分页参数
const page = ref(1)
const pageSize = ref(20)
const finished = ref(false)

// Fetch data
const fetchData = async (isLoadMore = false) => {
    if (loading.value) return

    try {
        loading.value = true
        const params = {
            pageNum: page.value,
            pageSize: pageSize.value
        }

        if (searchName.value) {
            params.name = searchName.value
        }

        if (selectedArea.value) {
            params.area = selectedArea.value
        }

        const res = await getMyStationList(params)
        if (res.code === 200) {
            const newList = res.data.list || res.data || []
            const currentList = Array.isArray(newList) ? newList : []

            if (isLoadMore) {
                list.value.push(...currentList)
            } else {
                list.value = currentList
            }

            // 判断是否还有更多数据
            if (currentList.length < pageSize.value) {
                finished.value = true
            }

            return currentList.length > 0 ? 'ok' : 'empty'
        }
        return 'error'
    } catch (error) {
        console.error('Fetch station list failed:', error)
        return 'error'
    } finally {
        loading.value = false
    }
}

// 加载更多
const loadMore = async ({ done }) => {
    if (finished.value) {
        done('empty')
        return
    }

    if (list.value.length > 0) {
        page.value++
    }

    const status = await fetchData(list.value.length > 0)
    done(status)
}

// 搜索处理
const handleSearch = () => {
    page.value = 1
    finished.value = false
    list.value = []
    fetchData(false)
}

// 筛选条件改变
const handleFilterChange = () => {
    page.value = 1
    finished.value = false
    list.value = []
    fetchData(false)
}

const handleSelect = (station) => {
    emit('change', station.stationId)
    menu.value = false
}

watch(menu, (val) => {
    if (val) {
        // Reset search conditions when opened
        searchName.value = ''
        selectedArea.value = null
        page.value = 1
        finished.value = false
        list.value = []
        fetchData(false)
    }
})
</script>

<style scoped lang="scss">
.station-selector-trigger {
    cursor: pointer;
    display: inline-block;
}

.station-selector-card {
    background: #fff;
    margin-top: 10px;
    border-radius: 15px 15px 15px 15px !important;
}

.gap-2 {
    gap: 8px;
}

.region-select {
    width: 30px;
    flex-shrink: 0;
    margin-top: -1px;
    /* 微调垂直对齐 */
}

.station-list {
    padding-bottom: 8px;
}

.station-item {
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    border-radius: 8px;
    font-size: 14px;
    color: #333;

    &:hover {
        background-color: #ECF0FF;
    }

    &.active {
        background-color: #e6f7ff;
        /* Light blue for active */
        color: #1890ff;
        font-weight: 500;
    }
}

.custom-scrollbar {
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }
}

:deep(.search-input .v-field__input),
:deep(.region-select .v-field__input),
:deep(.region-select .v-select__selection-text) {
    font-size: 14px !important;
    line-height: 24px !important;
}

:deep(.search-input input::placeholder) {
    font-size: 14px !important;
}

:deep(.v-field__outline) {
    border-radius: 16px !important;
    border: 1px solid #D4D9E5 !important;

    >div {
        display: none !important;
    }
}

.selector-header {
    background: linear-gradient(109deg, rgba(231, 237, 255, 0.40) 1.53%, rgba(255, 255, 255, 0.40) 98.28%);;
    border-radius: 15px 15px 0 0;
}
</style>
